import { Controller } from "@hotwired/stimulus"

// Self-storage unit-mix revenue calculator. Sums monthly/annual potential and
// revenue per square foot across a fixed set of common unit sizes. Each row
// carries its square footage in data-sqft and holds count + rate inputs.
// Pure arithmetic, recomputed live. Illustrative only — see disclaimer.
export default class extends Controller {
  static targets = [
    "row", "occupancy", "occLabel",
    "totalUnits", "totalSqft", "blended", "best",
    "grossMonthly", "effMonthly", "effAnnual"
  ]

  connect() { this.compute() }

  compute() {
    const occ = this.#clamp(this.#num(this.occupancyTarget), 0, 100)
    if (this.hasOccLabelTarget) this.occLabelTarget.textContent = Math.round(occ)

    let totalUnits = 0
    let totalSqft = 0
    let grossMonthly = 0
    let bestLabel = "—"
    let bestPerSqft = -1

    this.rowTargets.forEach((row) => {
      const sqft = Math.max(0, parseFloat(row.dataset.sqft) || 0)
      const count = Math.max(0, this.#num(row.querySelector('[data-role="count"]')))
      const rate = Math.max(0, this.#num(row.querySelector('[data-role="rate"]')))
      const perSqft = sqft > 0 ? rate / sqft : 0

      const perSqftCell = row.querySelector('[data-role="persqft"]')
      if (perSqftCell) perSqftCell.textContent = rate > 0 ? this.#money2(perSqft) : "—"

      totalUnits += count
      totalSqft += count * sqft
      grossMonthly += count * rate

      if (count > 0 && perSqft > bestPerSqft) {
        bestPerSqft = perSqft
        bestLabel = row.querySelector("td")?.textContent?.trim() || "—"
      }
    })

    const blended = totalSqft > 0 ? grossMonthly / totalSqft : 0
    const effMonthly = grossMonthly * (occ / 100)
    const effAnnual = effMonthly * 12

    this.totalUnitsTarget.textContent = totalUnits.toLocaleString()
    this.totalSqftTarget.textContent = totalSqft.toLocaleString()
    this.blendedTarget.textContent = this.#money2(blended)
    this.bestTarget.textContent = bestLabel
    this.grossMonthlyTarget.textContent = this.#money(grossMonthly)
    this.effMonthlyTarget.textContent = this.#money(effMonthly)
    this.effAnnualTarget.textContent = this.#money(effAnnual)
  }

  #num(el) { if (!el) return 0; const v = parseFloat(el.value); return Number.isNaN(v) ? 0 : v }
  #clamp(v, lo, hi) { return Math.min(hi, Math.max(lo, v)) }
  #money(n) {
    return n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 })
  }
  #money2(n) {
    return n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 2 })
  }
}
